import * as THREE from "three/webgpu";
import {BehaviorSubject, type Observable} from 'rxjs';
import {isSketchLineObject, SketchArcObject, SketchPointObject,} from '../mesh/sketch';
import {TangentConstraint} from '../sketch/solver/constraint/relationDefinition';
import type {SketchDrawingManager} from "../manager/SketchDrawingManager";
import {SketchDrawingHandler, SketchDrawingMouseDownPayload} from './SketchDrawingHandler';
import {SketchDrawingTool} from './DrawingSharedTypes';
import {
    getSourceEdgeEndPoint,
    getSourceEdgeStartPoint,
    isTangentArcSourceEdge,
    resolveEdgeTravelTangentAtEndpoint,
    type TangentArcSourceEdge,
} from '../utils/sketch/resolveEdgeTravelTangentAtEndpoint';
import {normalizeArcAngles} from '../tool/normalizeArcAngles';
import {buildArcFromThreePoints2D} from '../tool/buildArcFromThreePoints2D';
import {SKETCH_GEOMETRY_TOLERANCE_CONFIG} from '../sketch/config/SketchGeometryToleranceConfig';
import {
    ScreenSpaceDashedLineNodeMaterial,
    syncScreenSpaceDashedLineGeometry,
    syncThinChainLineGeometry,
    ThinChainLIneNodeMaterial,
} from '../material';

/** 圆弧预览颜色。 */
const ARC_PREVIEW_COLOR = 0x53a9ff;
/** 草图线颜色（用于构造线等实体着色）。 */
const SKETCH_LINE_COLOR = 0x53a9ff;
/** 圆弧圆心阶段外轮廓屏幕空间虚线段长度。 */
const ARC_CENTER_GUIDE_DASH_SIZE = 7;
/** 圆弧圆心阶段外轮廓屏幕空间虚线间隔长度。 */
const ARC_CENTER_GUIDE_GAP_SIZE = 4;
/** 圆弧圆心阶段预览透明度。 */
const ARC_CENTER_GUIDE_OPACITY = 0.8;
/** 圆弧圆心阶段外轮廓采样分段数。 */
const ARC_CENTER_GUIDE_SEGMENT_COUNT = 96;
/** 圆弧圆心阶段半径引导细点画线线段长度。 */
const ARC_CENTER_RADIUS_GUIDE_DASH_SIZE = 22;
/** 圆弧圆心阶段半径引导细点画线间隔长度。 */
const ARC_CENTER_RADIUS_GUIDE_GAP_SIZE = 4;
/** 圆弧圆心阶段半径引导细点画线点段长度。 */
const ARC_CENTER_RADIUS_GUIDE_DOT_SIZE = 4;
/** 三点圆弧第一阶段 90 度圆弧预览采样分段数。 */
const ARC_THREE_POINT_FIRST_SEGMENT_COUNT = 48;
/** 三点圆弧第一阶段固定预览圆弧角度，按 SolidWorks 第一阶段交互保持顺时针 90 度。 */
const ARC_THREE_POINT_FIRST_SEGMENT_SWEEP_ANGLE = -Math.PI / 2;

/**
 * 创建圆弧预览屏幕空间虚线材质。
 * 说明：圆弧绘制阶段的引导线不属于最终实体，固定使用屏幕空间虚线避免缩放后变成实线。
 * @param points 当前折线点列。
 * @param materialName 材质调试名称。
 * @returns 屏幕空间虚线材质。
 */
function createArcDashedPreviewMaterial(
    points: THREE.Vector3[],
    materialName: string,
): ScreenSpaceDashedLineNodeMaterial {
    const material = new ScreenSpaceDashedLineNodeMaterial({
        color: ARC_PREVIEW_COLOR,
        linewidth: 1,
        dashSize: ARC_CENTER_GUIDE_DASH_SIZE,
        gapSize: ARC_CENTER_GUIDE_GAP_SIZE,
        opacity: ARC_CENTER_GUIDE_OPACITY,
        transparent: true,
        points,
    });
    Reflect.set(material, 'name', materialName);
    return material;
}

/**
 * 创建圆弧圆心阶段半径引导细点画线材质。
 * 说明：半径引导线用于提示圆心到当前鼠标点方向，应比外轮廓更轻，固定使用细点画线。
 * @param points 当前半径引导线点列。
 * @returns 屏幕空间细点画线材质。
 */
function createArcCenterRadiusGuideMaterial(points: THREE.Vector3[]): ThinChainLIneNodeMaterial {
    const material = new ThinChainLIneNodeMaterial({
        color: ARC_PREVIEW_COLOR,
        linewidth: 1,
        dashSize: ARC_CENTER_RADIUS_GUIDE_DASH_SIZE,
        gapSize: ARC_CENTER_RADIUS_GUIDE_GAP_SIZE,
        dotSize: ARC_CENTER_RADIUS_GUIDE_DOT_SIZE,
        opacity: ARC_CENTER_GUIDE_OPACITY,
        transparent: true,
        points,
    });
    Reflect.set(material, 'name', '圆弧圆心阶段预览-屏幕空间细点画线半径引导材质');
    return material;
}



/**
 * 切线弧起点解析结果。
 */
interface TangentArcStartInfo {
    /** 起点控制点对象。 */
    pointObject: SketchPointObject;
    /** 起点世界坐标。 */
    point: THREE.Vector3;
    /** 起点处应沿用的切线方向。 */
    tangent: THREE.Vector3;
    /** 提供切线方向的来源草图边（直线/圆弧/椭圆弧/抛物线/样条，新圆弧与之相切）。 */
    sourceEdge: TangentArcSourceEdge;
}

/**
 * 切线弧方向选择策略。
 */
enum TangentArcDirectionPolicy {
    /** 严格沿输入切线向量方向生成圆弧。 */
    Directional = 'directional',
    /** 在同一切线线上选择扫掠角更小的圆弧。 */
    SmallSweep = 'small-sweep',
}

/**
 * 切线弧几何数据。
 */
export interface TangentArcData {
    /** 圆心世界坐标。 */
    centerWorld: THREE.Vector3;
    /** 圆心局部二维坐标。 */
    centerLocal: THREE.Vector2;
    /** 起点局部二维坐标。 */
    startLocal: THREE.Vector2;
    /** 终点局部二维坐标。 */
    endLocal: THREE.Vector2;
    /** 圆弧半径。 */
    radius: number;
    /** 归一化后的起始角度。 */
    startAngle: number;
    /** 归一化后的终止角度。 */
    endAngle: number;
    /** 圆弧扫掠角度，单位为弧度。 */
    angleRange: number;
    /** 是否顺时针生成圆弧。 */
    clockwise: boolean;
    /** 圆弧扫掠角度，单位为角度。 */
    angle: number;
    /** 终点处的切线方向，用于连续切线弧。 */
    tangentAtEndWorld: THREE.Vector3;
}


/**
 * 圆弧绘制命令枚举。
 */
export enum SketchArcCommand {
    /** 圆心起点终点圆弧命令。 */
    CenterStartEnd = 'sketch-arc',
    /** 切线弧命令。 */
    Tangent = 'sketch-arc-tangent',
    /** 三点圆弧命令。 */
    ThreePoint = 'sketch-arc-3-point',
}

/**
 * 判断命令是否为圆弧绘制命令。
 * @param command 命令字符串。
 * @returns 是否为圆弧绘制命令。
 */
export function isSketchArcCommand(command: string): command is SketchArcCommand {
    return Object.values(SketchArcCommand).includes(command as SketchArcCommand);
}

/**
 * 圆弧绘制选项。
 */
export interface ArcOptions {
    /** 圆弧绘制模式。 */
    mode: SketchArcCommand;
    /** 是否为构造几何。 */
    forConstruction: boolean;
}

/**
 * 圆弧绘制参数。
 */
export interface ArcParameters {
    /** 圆心坐标。 */
    center?: [number, number, number];
    /** 圆心2D坐标。 */
    center2d?: [number, number];
    /** 起点坐标。 */
    start?: [number, number, number];
    /** 起点2D坐标。 */
    start2d?: [number, number];
    /** 终点坐标。 */
    end?: [number, number, number];
    /** 终点2D坐标。 */
    end2d?: [number, number];
    /** 半径。 */
    radius?: number;
    /** 角度。 */
    angle?: number;
}

/**
 * 草图圆弧预览处理器。
 * 说明：
 * 1. 仅负责圆弧工具预览；
 * 2. 不参与鼠标按下提交流程。
 */
export class SketchArcDrawingHandler extends SketchDrawingHandler {
    /** 当前处理器负责的工具类型。 */
    readonly tool: SketchDrawingTool = SketchDrawingTool.Arc;
    /** 圆弧工具选项。 */
    public arcOptions: ArcOptions = {
        mode: SketchArcCommand.CenterStartEnd,
        forConstruction: false,
    };
    /** 圆弧参数。 */
    public arcParameters: ArcParameters | null = null;
    /** 圆弧参数实时流（绘制预览时发射当前参数，绘制结束时发射 null）。 */
    private readonly arcParametersSubject: BehaviorSubject<ArcParameters | null> = new BehaviorSubject<ArcParameters | null>(null);
    /** 切线圆弧当前切线方向。 */
    public tangentArcDirection: THREE.Vector3 | null = null;

    /** 当前绘制点对应的捕捉来源点对象。 */
    private drawingPointTargets: Array<SketchPointObject | null> = [];


    /**
     * 当前切线弧首段的来源草图边（直线/圆弧/椭圆弧/抛物线/样条）。
     *
     * 说明：对齐 SOLIDWORKS——切线弧从已有实体端点起步时会自动登记`相切`关系。
     * 只在首段有效（后续链段与前一段圆弧相切属弧-弧相切，`TangentConstraint`
     * 运行时只支持线-曲线组合，故链段之间不重复登记）；首段登记后立即清空。
     */
    private tangentArcSourceEdge: TangentArcSourceEdge | null = null;

    /** 当前切线圆弧链起点。 */
    public arcChainStartPoint: THREE.Vector3 | null = null;
    /** 当前切线圆弧链已提交段数。 */
    public arcChainSegmentCount: number = 0;

    /** 圆弧圆心阶段外轮廓预览对象缓存。 */
    private arcCenterGuideCirclePreview: THREE.Line | null = null;
    /** 圆弧圆心阶段半径引导线预览对象缓存。 */
    private arcCenterGuideRadiusPreview: THREE.Line | null = null;
    /** 圆弧圆心阶段圆心局部坐标复用对象。 */
    private readonly arcCenterGuideLocalCenter = new THREE.Vector2();
    /** 圆弧圆心阶段当前鼠标点局部坐标复用对象。 */
    private readonly arcCenterGuideLocalCurrent = new THREE.Vector2();
    /** 圆弧圆心阶段外轮廓采样点复用数组。 */
    private readonly arcCenterGuideCirclePoints = Array.from(
        {length: ARC_CENTER_GUIDE_SEGMENT_COUNT + 1},
        () => new THREE.Vector3(),
    );
    /** 圆弧圆心阶段半径引导线采样点复用数组。 */
    private readonly arcCenterGuideRadiusPoints = [
        new THREE.Vector3(),
        new THREE.Vector3(),
    ];


    /** 三点圆弧第一阶段虚线预览对象缓存。 */
    private arcThreePointFirstSegmentPreviewLine: THREE.Line | null = null;
    /** 三点圆弧第一阶段圆心预览控制点缓存。 */
    private arcThreePointFirstSegmentCenterPreviewPoint: SketchPointObject | null = null;
    /** 三点圆弧第一阶段起点局部坐标复用对象。 */
    private readonly arcThreePointFirstSegmentLocalStart = new THREE.Vector2();
    /** 三点圆弧第一阶段当前点局部坐标复用对象。 */
    private readonly arcThreePointFirstSegmentLocalCurrent = new THREE.Vector2();
    /** 三点圆弧第一阶段圆心局部坐标复用对象。 */
    private readonly arcThreePointFirstSegmentLocalCenter = new THREE.Vector2();
    /** 三点圆弧第一阶段圆心世界坐标复用对象。 */
    private readonly arcThreePointFirstSegmentCenterWorld = new THREE.Vector3();
    /** 三点圆弧第一阶段 90 度圆弧采样点复用数组。 */
    private readonly arcThreePointFirstSegmentPoints = Array.from(
        {length: ARC_THREE_POINT_FIRST_SEGMENT_COUNT + 1},
        () => new THREE.Vector3(),
    );


    /**
     * 构造函数。
     * @param sketchDrawingManager 草图绘制管理器实例。
     */
    constructor(sketchDrawingManager: SketchDrawingManager) {
        super(sketchDrawingManager);
    }

    /**
     * 观测圆弧参数实时变化流。
     * @returns 可观察的圆弧参数流，绘制中发射 ArcParameters，绘制结束发射 null。
     */
    public observeArcParameters(): Observable<ArcParameters | null> {
        return this.arcParametersSubject.asObservable();
    }

    
    getRequiredPointCount(): number {
        return this.arcOptions.mode === SketchArcCommand.Tangent ? 2 : 3;
    }

    
    get closable(): boolean {
        return this.arcOptions.mode === SketchArcCommand.Tangent;
    }

    
    resetState(): void {
        this.drawingPoints = [];
        this.arcParameters = null;
        this.arcParametersSubject.next(null);
        this.tangentArcDirection = null;

        this.drawingPointTargets = [];


        this.tangentArcSourceEdge = null;

        this.arcChainStartPoint = null;
        this.arcChainSegmentCount = 0;

        this.clearArcCenterGuidePreviewCache();


        this.clearArcThreePointFirstSegmentPreviewCache();

    }

    /**
     * 处理圆弧绘制鼠标按下事件。
     * @param payload 鼠标按下载荷。

     */
    handleMouseDown(payload: SketchDrawingMouseDownPayload): void {
        const event = payload.event;

        if (this.arcOptions.mode === SketchArcCommand.Tangent && this.drawingPoints.length === 0) {
            const startInfo = this.resolveTangentArcStartInfo(payload.snappedTargetPoint);
            if (!startInfo) {
                this.viewer.drawingNoticeCallback?.('请选择草图实体的一个端点', 'warning');
                return;
            }
            this.tangentArcDirection = startInfo.tangent.clone();

            this.tangentArcSourceEdge = startInfo.sourceEdge;

            this.arcChainStartPoint = startInfo.point.clone();
            this.arcChainSegmentCount = 0;
            this.drawingPoints.push(startInfo.point.clone());

            this.drawingPointTargets.push(startInfo.pointObject);

            this.sketchDrawingManager.setPreviewPoint(startInfo.point.clone());
            this.updatePreview(startInfo.point.clone());
            return;
        }


        // 普通圆弧/相切圆弧的后续点击都应优先使用上游已解析的 snappedPoint，
        // 保证圆弧端点与其他草图图元保持一致的捕捉落点行为。
        let point = payload.snappedPoint ?? this.getDrawingPoint(event);


        let shouldFinishArcChain = false;
        if (this.arcOptions.mode === SketchArcCommand.Tangent) {
            const chainStartPoint = this.resolveChainStartPointForClosure();
            const canSnapToStart = this.canSnapToClosureStart();
            const snappedToStart = Boolean(
                chainStartPoint
                && canSnapToStart
                && this.isWithinClosureTolerance(point, chainStartPoint),
            );
            if (snappedToStart && chainStartPoint) {
                // 命中闭合起点时用链起点精确坐标覆盖点击点，避免微小捕捉误差破坏闭合链。
                point = chainStartPoint.clone();
            }
            if (this.arcChainStartPoint && snappedToStart && this.arcChainSegmentCount >= 1) {
                shouldFinishArcChain = true;
            }
        }

        this.drawingPoints.push(point.clone());

        this.drawingPointTargets.push(payload.snappedTargetPoint ?? null);

        if (this.arcOptions.mode === SketchArcCommand.Tangent) {
            const tangentDrawingPoints = this.drawingPoints;
            if (tangentDrawingPoints.length === 2 && this.tangentArcDirection) {
                const result = this.createTangentArc(
                    tangentDrawingPoints[0],
                    tangentDrawingPoints[1],
                    this.tangentArcDirection,
                );
                if (!result) {
                    this.drawingPoints.pop();
                    return;
                }

                // 新圆弧提交后立即尝试统一混合闭合收口；成功时结束当前切线弧链，
                // 避免继续从已经归属闭合集合的端点创建后续圆弧。
                if (this.tryFinalizeClosedArcCollection(result.line)) {
                    this.finishTangentArcDrawing();
                    return;
                }
                this.arcChainSegmentCount += 1;
                this.drawingPoints = [];

                this.drawingPointTargets = [];

                this.drawingPoints.push(result.endPoint.clone());

                this.drawingPointTargets.push(result.line.getEndPoint());

                this.tangentArcDirection = result.tangentAtEnd.clone();
                this.destroyPreview();
            }
            if (shouldFinishArcChain) {
                this.finishTangentArcDrawing();
            }
            return;
        }

        if (this.drawingPoints.length === this.getArcPointCount(this.arcOptions.mode)) {
            this.createArcEntityByHandler();
            this.drawingPoints = [];

            this.drawingPointTargets = [];

            this.destroyPreview();
        }
    }


    /**
     * 处理切线弧双击结束事件。
     * 说明：
     * 1. 切线弧支持单击连续绘制，不能用 mouseDown.detail 结束绘制；
     * 2. 只有浏览器独立 dblclick 事件才结束当前切线弧链；
     * 3. 双击只清理绘制状态，不删除已经创建的圆弧实体。
     * @param _payload 鼠标双击载荷。
     */
    override handleDoubleClick(_payload: SketchDrawingMouseDownPayload): void {
        if (this.arcOptions.mode !== SketchArcCommand.Tangent || this.drawingPoints.length === 0) {
            return;
        }
        this.finishTangentArcDrawing();
    }


    /**
     * 获取圆弧工具绘制提示。
     * @returns 提示文本。
     */
    getDrawingHint(): string {
        const pointCount = this.drawingPoints.length;
        let baseHint = '第1步：点击圆心 → 第2步：点击第一个边缘点 → 第3步：点击第二个边缘点（顺时针）';
        if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击圆心';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击起点';
            } else if (pointCount === 2) {
                baseHint = '步骤 3/3：点击终点（顺时针）';
            }
        } else if (this.arcOptions.mode === SketchArcCommand.ThreePoint) {
            if (pointCount === 0) {
                baseHint = '步骤 1/3：点击起点';
            } else if (pointCount === 1) {
                baseHint = '步骤 2/3：点击终点';
            } else if (pointCount === 2) {
                baseHint = '步骤 3/3：点击圆弧上的点';
            }
        } else if (this.arcOptions.mode === SketchArcCommand.Tangent) {
            baseHint = pointCount === 0
                ? '请选择草图实体的一个端点'
                : '步骤 2/2：点击终点（双击结束）';
        }

        let extraInfo = '';
        if (pointCount > 0 && this.sketchDrawingManager.getPreviewPoint()) {
            if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
                const radius = this.drawingPoints[0].distanceTo(
                    pointCount >= 2 ? this.drawingPoints[1] : this.sketchDrawingManager.getPreviewPoint(),
                );
                extraInfo += ` - 半径: ${radius.toFixed(2)}`;
                if (pointCount === 2) {
                    const referencePlane = this.viewer.currentReferencePlane
                    const localCenter = referencePlane.worldToLocal(this.drawingPoints[0]);
                    const local1 = referencePlane.worldToLocal(this.drawingPoints[1]);
                    const local2 = referencePlane.worldToLocal(this.sketchDrawingManager.getPreviewPoint());
                    const angle1 = Math.atan2(local1.y - localCenter.y, local1.x - localCenter.x);
                    let angle2 = Math.atan2(local2.y - localCenter.y, local2.x - localCenter.x);
                    if (angle2 > angle1) {
                        angle2 -= Math.PI * 2;
                    }
                    const angleRange = Math.abs(angle1 - angle2) * 180 / Math.PI;
                    extraInfo += `, 角度: ${angleRange.toFixed(1)}°`;
                }
            } else if (this.arcOptions.mode === SketchArcCommand.ThreePoint && pointCount >= 2) {
                const referencePlane = this.viewer.currentReferencePlane
                const local1 = referencePlane.worldToLocal(this.drawingPoints[0]);
                const local2 = referencePlane.worldToLocal(this.drawingPoints[1]);
                const local3 = referencePlane.worldToLocal(this.sketchDrawingManager.getPreviewPoint());
                const arcData = buildArcFromThreePoints2D(local1, local2, local3);
                if (arcData) {
                    extraInfo += ` - 半径: ${arcData.radius.toFixed(2)}, 角度: ${arcData.angle.toFixed(1)}°`;
                }
            } else if (this.arcOptions.mode === SketchArcCommand.Tangent && this.tangentArcDirection && pointCount >= 1) {
                const referencePlane = this.viewer.currentReferencePlane
                const drawingPoints = this.drawingPoints;
                const previewPoint = this.sketchDrawingManager.getPreviewPoint();
                if (drawingPoints.length >= 1 && previewPoint) {
                    const arcData = this.buildTangentArcData(
                        drawingPoints[0],
                        previewPoint,
                        this.tangentArcDirection,
                    );
                    if (arcData) {
                        extraInfo += ` - 半径: ${arcData.radius.toFixed(2)}, 角度: ${arcData.angle.toFixed(1)}°`;
                    }
                }
            }
        }

        const actionHint = this.arcOptions.mode === SketchArcCommand.Tangent && pointCount >= 1
            ? '双击结束 | ESC取消'
            : 'ESC取消';
        return `${baseHint}${extraInfo} | ${actionHint}`;
    }


    /**
     * 更新圆弧预览。

     * @param currentPoint 当前预览点。
     */
    updateArcPreviewByHandler(currentPoint: THREE.Vector3): void {
        if (this.drawingPoints.length === 0) {
            return;
        }
        const firstPoint = this.drawingPoints[0];
        if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
            if (this.drawingPoints.length === 1) {
                this.createCirclePreviewByCenterInHandler(firstPoint, currentPoint);
                // 首段阶段实时同步参数面板数据，保持旧行为一致。
                const radius = firstPoint.distanceTo(currentPoint);
                this.updateArcParameters(
                    firstPoint,
                    currentPoint,
                    currentPoint,
                    radius,
                    0,
                );
                return;
            }
            if (this.drawingPoints.length === 2) {
                this.createArcPreviewByCenterInHandler(firstPoint, this.drawingPoints[1], currentPoint);
            }
            return;
        }

        if (this.arcOptions.mode === SketchArcCommand.ThreePoint) {
            if (this.drawingPoints.length === 1) {
                this.createThreePointFirstSegmentPreviewInHandler(firstPoint, currentPoint);
                this.arcParameters = null;
                this.arcParametersSubject.next(null);
                return;
            }
            if (this.drawingPoints.length === 2) {
                this.createArcPreviewByThreePointsInHandler(firstPoint, this.drawingPoints[1], currentPoint);
            }
            return;
        }

        if (this.arcOptions.mode === SketchArcCommand.Tangent && this.tangentArcDirection) {
            this.createArcPreviewByTangentInHandler(firstPoint, currentPoint, this.tangentArcDirection);
        }
    }


    /**
     * 统一预览更新入口。

     * @param currentPoint 当前预览点。
     */
    updatePreview(currentPoint: THREE.Vector3): void {
        this.updateArcPreviewByHandler(currentPoint);
    }

    /**
     * 获取圆弧绘制所需的点数。
     * @param mode 圆弧模式。
     * @returns 所需点数。
     */
    public getArcPointCount(mode: ArcOptions['mode']): number {
        return mode === SketchArcCommand.Tangent ? 2 : 3;
    }

    /**
     * 更新圆弧参数面板数据。
     * @param centerWorld 圆心世界坐标。
     * @param startWorld 起点世界坐标。
     * @param endWorld 终点世界坐标。
     * @param radius 半径。
     * @param angleRange 角度范围（弧度）。
     */
    public updateArcParameters(
        centerWorld: THREE.Vector3,
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        radius: number,
        angleRange: number,
    ): void {
        const round = (value: number): number => Number(value.toFixed(8));
        const centerLocal = this.referencePlane.worldToLocal(centerWorld);
        const startLocal = this.referencePlane.worldToLocal(startWorld);
        const endLocal = this.referencePlane.worldToLocal(endWorld);
        this.arcParameters = {
            center: centerWorld.toArray() as [number, number, number],
            center2d: [round(centerLocal.x), round(centerLocal.y)],
            start: startWorld.toArray() as [number, number, number],
            start2d: [round(startLocal.x), round(startLocal.y)],
            end: endWorld.toArray() as [number, number, number],
            end2d: [round(endLocal.x), round(endLocal.y)],
            radius: round(radius),
            angle: round((angleRange * 180) / Math.PI),
        };
        this.arcParametersSubject.next(this.arcParameters);
    }

    /**
     * 构建切线圆弧的几何数据。
     * @param startWorld 起点世界坐标。
     * @param endWorld 终点世界坐标。
     * @param tangentWorld 起点切线方向（世界坐标）。
     * @returns 切线圆弧几何数据，或 null 表示无法构建。
     */
    public buildTangentArcData(
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        tangentWorld: THREE.Vector3,
    ): TangentArcData | null {
        return this.buildTangentArcDataByPolicy(
            startWorld,
            endWorld,
            tangentWorld,
            TangentArcDirectionPolicy.Directional,
        );
    }


    /**
     * 按指定策略构建切线圆弧几何数据。
     * 说明：
     * 1. 首段切线弧需要严格沿线段端点延伸方向起步；
     * 2. 连续第二段及之后更接近 SolidWorks 的切线关系体验，应避免自动绕成接近整圆的大圆弧；
     * 3. 因此小角度策略会同时评估输入切线方向与反向切线方向，并选择扫掠角更小的候选。
     * @param startWorld 起点世界坐标。
     * @param endWorld 终点世界坐标。
     * @param tangentWorld 起点切线方向（世界坐标）。
     * @param directionPolicy 切线弧方向选择策略。
     * @returns 切线圆弧几何数据，或 null 表示无法构建。
     */
    private buildTangentArcDataByPolicy(
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        tangentWorld: THREE.Vector3,
        directionPolicy: TangentArcDirectionPolicy,
    ): TangentArcData | null {
        const primaryData = this.buildDirectionalTangentArcData(startWorld, endWorld, tangentWorld);
        if (directionPolicy === TangentArcDirectionPolicy.Directional) {
            return primaryData;
        }

        const reversedData = this.buildDirectionalTangentArcData(
            startWorld,
            endWorld,
            tangentWorld.clone().multiplyScalar(-1),
        );

        if (!primaryData) {
            return reversedData;
        }
        if (!reversedData) {
            return primaryData;
        }

        // 连续切线弧以小扫掠角为准，避免鼠标落在切线反向侧时绕行成接近整圆。
        return reversedData.angleRange < primaryData.angleRange
            ? reversedData
            : primaryData;
    }

    /**
     * 按输入切线向量的有向方向构建切线圆弧几何数据。
     * @param startWorld 起点世界坐标。
     * @param endWorld 终点世界坐标。
     * @param tangentWorld 起点切线方向（世界坐标）。
     * @returns 切线圆弧几何数据，或 null 表示无法构建。
     */
    private buildDirectionalTangentArcData(
        startWorld: THREE.Vector3,
        endWorld: THREE.Vector3,
        tangentWorld: THREE.Vector3,
    ): TangentArcData | null {
        // 转换到局部坐标系。
        const startLocalRaw = this.referencePlane.worldToLocal(startWorld);
        const endLocalRaw = this.referencePlane.worldToLocal(endWorld);
        const startLocal = new THREE.Vector2(startLocalRaw.x, startLocalRaw.y);
        const endLocal = new THREE.Vector2(endLocalRaw.x, endLocalRaw.y);
        // 将切线方向投射到绘图平面的局部2D空间。
        const tangentLocal = new THREE.Vector2(
            tangentWorld.dot(this.referencePlane.xAxis),
            tangentWorld.dot(this.referencePlane.yAxis)
        );
        if (tangentLocal.lengthSq() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared) {
            return null;
        }
        tangentLocal.normalize();

        // 计算起点到终点的向量。
        const d = new THREE.Vector2(startLocal.x - endLocal.x, startLocal.y - endLocal.y);
        const dd = d.lengthSq();
        if (dd < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLengthSquared) {
            return null;
        }

        // 尝试两个法线方向，寻找有效的圆心候选。
        const normals = [
            new THREE.Vector2(-tangentLocal.y, tangentLocal.x),
            new THREE.Vector2(tangentLocal.y, -tangentLocal.x),
        ];

        const candidates: Array<{ center: THREE.Vector2; radius: number }> = [];
        for (const normal of normals) {
            const denom = 2 * (d.dot(normal));
            if (Math.abs(denom) < SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
                continue;
            }
            const r = -dd / denom;
            if (!Number.isFinite(r) || r <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
                continue;
            }
            const center = new THREE.Vector2(
                startLocal.x + normal.x * r,
                startLocal.y + normal.y * r
            );
            candidates.push({center, radius: r});
        }

        if (candidates.length === 0) {
            return null;
        }

        // 选择半径最小的候选方案。
        let chosen = candidates[0];
        if (candidates.length > 1) {
            chosen = candidates.reduce((best, current) => (current.radius < best.radius ? current : best), candidates[0]);
        }

        const centerLocal = chosen.center.clone();
        const radius = chosen.radius;
        const startAngleRaw = Math.atan2(startLocal.y - centerLocal.y, startLocal.x - centerLocal.x);
        const endAngleRaw = Math.atan2(endLocal.y - centerLocal.y, endLocal.x - centerLocal.x);

        // 根据切线方向判断顺时针/逆时针。
        const radiusVec = new THREE.Vector2(startLocal.x - centerLocal.x, startLocal.y - centerLocal.y).normalize();
        const tangentCcw = new THREE.Vector2(-radiusVec.y, radiusVec.x);
        const tangentCw = new THREE.Vector2(radiusVec.y, -radiusVec.x);
        const clockwise = tangentLocal.dot(tangentCw) >= tangentLocal.dot(tangentCcw);

        // 归一化角度。
        const normalized = normalizeArcAngles(startAngleRaw, endAngleRaw, clockwise);

        // 计算终点处的切线方向（用于链式切线圆弧）。
        const endRadiusVec = new THREE.Vector2(endLocal.x - centerLocal.x, endLocal.y - centerLocal.y).normalize();
        const tangentAtEndLocal = clockwise
            ? new THREE.Vector2(endRadiusVec.y, -endRadiusVec.x)
            : new THREE.Vector2(-endRadiusVec.y, endRadiusVec.x);
        const tangentAtEndWorld = this.referencePlane.xAxis.clone()
            .multiplyScalar(tangentAtEndLocal.x)
            .addScaledVector(this.referencePlane.yAxis, tangentAtEndLocal.y)
            .normalize();

        return {
            centerWorld: this.referencePlane.localToWorld(new THREE.Vector2(centerLocal.x, centerLocal.y)),
            centerLocal,
            startLocal,
            endLocal,
            radius,
            startAngle: normalized.start,
            endAngle: normalized.end,
            angleRange: normalized.angleRange,
            clockwise,
            angle: normalized.angleRange * 180 / Math.PI,
            tangentAtEndWorld,
        };
    }

    /**
     * 解析当前切线弧应使用的方向选择策略。
     * @returns 当前绘制阶段对应的方向选择策略。
     */
    private resolveTangentArcDirectionPolicy(): TangentArcDirectionPolicy {
        if (this.arcOptions.mode === SketchArcCommand.Tangent && this.arcChainSegmentCount > 0) {
            return TangentArcDirectionPolicy.SmallSweep;
        }
        return TangentArcDirectionPolicy.Directional;
    }


    /**
     * 创建切线圆弧实体。
     * @param startPoint 起点世界坐标。
     * @param endPoint 终点世界坐标。
     * @param tangentDirection 起点切线方向。
     * @returns 创建的圆弧信息，或 null 表示创建失败。
     */
    public createTangentArc(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        tangentDirection: THREE.Vector3,
    ): { line: SketchArcObject; endPoint: THREE.Vector3; tangentAtEnd: THREE.Vector3 } | null {
        // 构建切线圆弧几何数据。
        const arcData = this.buildTangentArcDataByPolicy(
            startPoint,
            endPoint,
            tangentDirection,
            this.resolveTangentArcDirectionPolicy(),
        );
        if (!arcData) {
            return null;
        }

        // 构建圆弧世界坐标点。
        const arcPoints = this.buildArcWorldPoints(
            arcData.centerLocal,
            arcData.radius,
            arcData.startAngle,
            arcData.endAngle,
            arcData.clockwise,
        );

        // 创建材质：构造线使用虚线，普通线使用实线。
        const isConstruction = this.arcOptions.forConstruction;
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: SKETCH_LINE_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.9,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({color: SKETCH_LINE_COLOR, linewidth: 2});
        // 解析参考平面并创建控制点。
        const startControlPoint = this.resolveReusableArcControlPoint(0, startPoint)
            ?? this.createLineControlPoint(
                startPoint,
                false,
                this.resolveArcControlPointSource(0, startPoint),
            );
        const endControlPoint = this.resolveReusableArcControlPoint(1, endPoint)
            ?? this.createLineControlPoint(
                endPoint,
                false,
                this.resolveArcControlPointSource(1, endPoint),
            );
        const centerControlPoint = this.createLineControlPoint(arcData.centerWorld,);
        // 创建 SketchArcObject 实体。
        const line = new SketchArcObject({
            startPoint: startControlPoint,
            endPoint: endControlPoint,
            centerPoint: centerControlPoint,
            material,
            referencePlane: this.referencePlane,
            definition: {
                centerWorld: arcData.centerWorld.clone(),
                radius: arcData.radius,
                startAngle: arcData.startAngle,
                endAngle: arcData.endAngle,
                clockwise: arcData.clockwise,
                segments: 64,
            },
        });
        line.updateArc();


        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(startControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(endControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(centerControlPoint);

        // 添加到草图实体集合并通知创建事件。
        this.viewer.addSketchObject(line);

        this.applyTangentArcSourceRelation(line);

        this.sketchDrawingManager.notifySketchEntityCreated(line);
        // 更新圆弧参数面板。
        this.updateArcParameters(
            arcData.centerWorld,
            startPoint,
            endPoint,
            arcData.radius,
            arcPoints.angleRange,
        );
        // 创建完成后程序化选中新圆弧，使属性面板参数可编辑。
        this.viewer.entitySelectionManager?.selectEntityProgrammatically(line);

        return {
            line,
            endPoint: endPoint.clone(),
            tangentAtEnd: arcData.tangentAtEndWorld.clone(),
        };
    }


    /**
     * 为切线弧首段登记与来源草图边（直线/圆弧/椭圆弧/抛物线/样条）的`相切`关系。
     *
     * 说明：
     * 1. 对齐 SOLIDWORKS——切线弧从已有实体端点起步会自动生成`相切`几何关系，
     *    此前只借用来源边算切线方向、不落关系，改动来源边后圆弧不再跟随；
     * 2. 关系由构造保证已满足，故只挂载不触发求解，避免创建瞬间挪动几何
     *    （与 `SketchLineDrawingHandler.applyLineDirectionRelation` 同范式）；
     * 3. 受"自动几何关系"开关约束；关系创建落在绘制事务内，撤销/存盘随之收口；
     * 4. 曲线来源（圆弧/椭圆弧/抛物线/样条）时，`TangentConstraint` 运行时仅对
     *    「线类 + 圆类」组合求解，曲线-曲线组合挂载后由
     *    `SketchArcObject` 的相切锚定端点拖拽重建收口（见 `rebuildTangentAnchoredArcDefinition`）。
     *
     * @param arc 新建切线圆弧。
     */
    private applyTangentArcSourceRelation(arc: SketchArcObject): void {
        const sourceEdge = this.tangentArcSourceEdge;

        // 只有首段有来源边；登记后立即清空，链上后续段不重复挂载。
        this.tangentArcSourceEdge = null;

        if (this.arcOptions.mode !== SketchArcCommand.Tangent
            || !sourceEdge
            || !this.isAutoGeometryRelationsEnabled()) {
            return;
        }

        const tangentConstraint = new TangentConstraint();

        sourceEdge.constraints.addRelation(
            tangentConstraint as unknown as Parameters<typeof sourceEdge.constraints.addRelation>[0],
        );
        arc.constraints.addRelation(
            tangentConstraint as unknown as Parameters<typeof arc.constraints.addRelation>[0],
        );
    }


    /**
     * 完成切线圆弧绘制，清理绘制状态。
     */
    public finishTangentArcDrawing(): void {
        if ((this.sketchDrawingManager.getCurrentTool() || null) !== SketchDrawingTool.Arc || this.arcOptions.mode !== SketchArcCommand.Tangent) {
            return;
        }
        // 清理绘制点和预览。
        this.drawingPoints = [];

        this.drawingPointTargets = [];

        this.sketchDrawingManager.setPreviewPoint(null);
        this.destroyPreview();
        // 清理切线圆弧链状态。
        this.tangentArcDirection = null;
        this.arcChainStartPoint = null;
        this.arcChainSegmentCount = 0;
    }


    /**
     * 销毁当前绘制预览并清空圆弧圆心阶段缓存引用。
     * 说明：圆弧圆心阶段会复用预览对象；一旦进入其他阶段或清理绘制状态，缓存必须同步失效。
     */
    protected override destroyPreview(): void {
        super.destroyPreview();
        this.clearArcCenterGuidePreviewCache();
        this.clearArcThreePointFirstSegmentPreviewCache();
    }

    /**
     * 清空圆弧圆心阶段预览缓存引用。
     */
    private clearArcCenterGuidePreviewCache(): void {
        this.arcCenterGuideCirclePreview = null;
        this.arcCenterGuideRadiusPreview = null;
    }

    /**
     * 判断圆弧圆心阶段预览是否仍可复用。
     * @returns 当前缓存对象是否仍由绘制管理器持有。
     */
    private isArcCenterGuidePreviewActive(): boolean {
        return Boolean(
            this.arcCenterGuideCirclePreview
            && this.arcCenterGuideRadiusPreview
            && this.sketchDrawingManager.previewCircle === this.arcCenterGuideCirclePreview
            && this.sketchDrawingManager.previewLine === this.arcCenterGuideRadiusPreview,
        );
    }


    /**
     * 清空三点圆弧第一阶段虚线预览缓存引用。
     */
    private clearArcThreePointFirstSegmentPreviewCache(): void {
        const centerPreviewPoint = this.arcThreePointFirstSegmentCenterPreviewPoint;
        if (centerPreviewPoint) {
            // 圆心预览控制点由三点圆弧处理器自主管理，不能依赖 previewPoints 统一清理。
            centerPreviewPoint.parent?.remove(centerPreviewPoint);
            this.viewer.scene?.remove(centerPreviewPoint);
        }
        this.arcThreePointFirstSegmentPreviewLine = null;
        this.arcThreePointFirstSegmentCenterPreviewPoint = null;
    }

    /**
     * 判断三点圆弧第一阶段虚线预览是否仍可复用。
     * @returns 当前缓存线是否仍由绘制管理器持有。
     */
    private isArcThreePointFirstSegmentPreviewActive(): boolean {
        return Boolean(
            this.arcThreePointFirstSegmentPreviewLine
            && this.arcThreePointFirstSegmentCenterPreviewPoint
            && this.sketchDrawingManager.previewLine === this.arcThreePointFirstSegmentPreviewLine,
        );
    }

    /**
     * 创建三点圆弧第一阶段虚线预览。
     * 说明：
     * 1. 第一阶段按 SolidWorks 表现生成固定 90 度临时圆弧；
     * 2. 使用虚线与最终圆弧实体预览区分，避免用户误认为已创建正式圆弧。
     * @param start 起点世界坐标。
     * @param currentPoint 当前鼠标世界坐标。
     */
    private createThreePointFirstSegmentPreviewInHandler(
        start: THREE.Vector3,
        currentPoint: THREE.Vector3,
    ): void {
        const previewState = this.updateArcThreePointFirstSegmentPreviewState(start, currentPoint);
        if (!previewState) {
            this.destroyPreview();
            return;
        }

        if (!this.isArcThreePointFirstSegmentPreviewActive()) {
            this.destroyPreview();
            this.sketchDrawingManager.previewLine = this.createArcThreePointFirstSegmentLine(previewState.points);
            this.arcThreePointFirstSegmentPreviewLine = this.sketchDrawingManager.previewLine;
            this.viewer.addObject(this.sketchDrawingManager.previewLine);

            this.arcThreePointFirstSegmentCenterPreviewPoint = this.createLineControlPoint(
                previewState.centerWorld,
                true,
            );
            this.arcThreePointFirstSegmentCenterPreviewPoint.name = '三点圆弧第一阶段圆心预览控制点';
            this.viewer.addObject(this.arcThreePointFirstSegmentCenterPreviewPoint);
            return;
        }

        this.updateScreenSpaceDashedPreviewLine(
            this.arcThreePointFirstSegmentPreviewLine as THREE.Line,
            previewState.points,
        );
        this.arcThreePointFirstSegmentCenterPreviewPoint?.position.copy(previewState.centerWorld);
        this.arcThreePointFirstSegmentCenterPreviewPoint?.updateMatrixWorld(true);
    }

    /**
     * 原地更新三点圆弧第一阶段 90 度圆弧预览状态。
     * 说明：SolidWorks 在三点圆弧第一阶段用固定 90 度圆弧提示第二点候选，第三点仍决定最终圆弧。
     * @param start 起点世界坐标。
     * @param currentPoint 当前鼠标世界坐标。
     * @returns 90 度圆弧采样点与圆心世界坐标；两点退化时返回 null。
     */
    private updateArcThreePointFirstSegmentPreviewState(
        start: THREE.Vector3,
        currentPoint: THREE.Vector3,
    ): {points: THREE.Vector3[]; centerWorld: THREE.Vector3} | null {
        const localStart = this.referencePlane.worldToLocalTo(
            start,
            this.arcThreePointFirstSegmentLocalStart,
        );
        const localCurrent = this.referencePlane.worldToLocalTo(
            currentPoint,
            this.arcThreePointFirstSegmentLocalCurrent,
        );
        const chordLength = localStart.distanceTo(localCurrent);
        if (!Number.isFinite(chordLength) || chordLength <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            return null;
        }

        const chordX = localCurrent.x - localStart.x;
        const chordY = localCurrent.y - localStart.y;
        const midpointX = (localStart.x + localCurrent.x) * 0.5;
        const midpointY = (localStart.y + localCurrent.y) * 0.5;
        const centerOffset = chordLength * 0.5;
        const normalX = chordY / chordLength;
        const normalY = -chordX / chordLength;
        const centerLocal = this.arcThreePointFirstSegmentLocalCenter.set(
            midpointX + normalX * centerOffset,
            midpointY + normalY * centerOffset,
        );
        const radius = chordLength / Math.SQRT2;
        const startAngle = Math.atan2(localStart.y - centerLocal.y, localStart.x - centerLocal.x);

        for (let index = 0; index <= ARC_THREE_POINT_FIRST_SEGMENT_COUNT; index += 1) {
            const t = index / ARC_THREE_POINT_FIRST_SEGMENT_COUNT;
            const angle = startAngle + ARC_THREE_POINT_FIRST_SEGMENT_SWEEP_ANGLE * t;
            const x = centerLocal.x + radius * Math.cos(angle);
            const y = centerLocal.y + radius * Math.sin(angle);
            // 第一阶段预览属于高频鼠标路径，直接写入复用采样点，避免反复创建 Vector3。
            this.referencePlane.localToWorldTo(x, y, this.arcThreePointFirstSegmentPoints[index]);
        }

        this.referencePlane.localToWorldTo(centerLocal, this.arcThreePointFirstSegmentCenterWorld);

        return {
            points: this.arcThreePointFirstSegmentPoints,
            centerWorld: this.arcThreePointFirstSegmentCenterWorld,
        };
    }

    /**
     * 创建三点圆弧第一阶段虚线对象。
     * @param points 预览线点列。
     * @returns 三点圆弧第一阶段虚线对象。
     */
    private createArcThreePointFirstSegmentLine(points: THREE.Vector3[]): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const previewLine = new THREE.Line(
            geometry,
            createArcDashedPreviewMaterial(points, '三点圆弧第一阶段预览-屏幕空间虚线材质'),
        );
        syncScreenSpaceDashedLineGeometry(previewLine);
        return previewLine;
    }


    /**
     * 在处理器内创建“圆心+半径”圆预览。
     */
    private createCirclePreviewByCenterInHandler(center: THREE.Vector3, edge: THREE.Vector3): void {
        const localCenter = this.referencePlane.worldToLocalTo(center, this.arcCenterGuideLocalCenter);
        const localCurrent = this.referencePlane.worldToLocalTo(edge, this.arcCenterGuideLocalCurrent);
        const radius = localCenter.distanceTo(localCurrent);
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }

        // 第一点后的完整圆只表达半径范围，外轮廓和半径线都必须作为临时引导处理。
        const guideCirclePoints = this.updateArcCenterGuideCirclePoints(localCenter, radius);
        const guideRadiusPoints = this.updateArcCenterGuideRadiusPoints(center, localCurrent);

        if (!this.isArcCenterGuidePreviewActive()) {
            this.destroyPreview();
            this.sketchDrawingManager.previewCircle = this.createArcCenterGuideCircle(guideCirclePoints);
            this.arcCenterGuideCirclePreview = this.sketchDrawingManager.previewCircle;
            this.viewer.addObject(this.sketchDrawingManager.previewCircle);

            this.sketchDrawingManager.previewLine = this.createArcCenterGuideRadiusLine(guideRadiusPoints);
            this.arcCenterGuideRadiusPreview = this.sketchDrawingManager.previewLine;
            this.viewer.addObject(this.sketchDrawingManager.previewLine);
            return;
        }

        // 鼠标移动属于高频路径，只更新已有几何和距离 attribute，避免反复创建材质与对象。
        this.updateScreenSpaceDashedPreviewLine(
            this.arcCenterGuideCirclePreview as THREE.Line,
            guideCirclePoints,
        );
        this.updateThinChainPreviewLine(
            this.arcCenterGuideRadiusPreview as THREE.Line,
            guideRadiusPoints,
        );
    }

    /**
     * 原地更新圆弧圆心阶段外轮廓采样点。
     * @param localCenter 圆心局部坐标。
     * @param radius 当前半径。
     * @returns 可写入预览几何的复用采样点数组。
     */
    private updateArcCenterGuideCirclePoints(
        localCenter: THREE.Vector2,
        radius: number,
    ): THREE.Vector3[] {
        for (let index = 0; index <= ARC_CENTER_GUIDE_SEGMENT_COUNT; index += 1) {
            const angle = (Math.PI * 2 * index) / ARC_CENTER_GUIDE_SEGMENT_COUNT;
            const x = localCenter.x + radius * Math.cos(angle);
            const y = localCenter.y + radius * Math.sin(angle);
            // 高频预览直接写入复用点，避免每次鼠标移动重新分配整圈 Vector3。
            this.referencePlane.localToWorldTo(x, y, this.arcCenterGuideCirclePoints[index]);
        }

        return this.arcCenterGuideCirclePoints;
    }

    /**
     * 原地更新圆弧圆心阶段半径引导线采样点。
     * @param center 圆心世界坐标。
     * @param localCurrent 当前鼠标投影到草图平面后的局部坐标。
     * @returns 可写入预览几何的复用采样点数组。
     */
    private updateArcCenterGuideRadiusPoints(
        center: THREE.Vector3,
        localCurrent: THREE.Vector2,
    ): THREE.Vector3[] {
        this.arcCenterGuideRadiusPoints[0].copy(center);
        this.referencePlane.localToWorldTo(localCurrent, this.arcCenterGuideRadiusPoints[1]);
        return this.arcCenterGuideRadiusPoints;
    }

    /**
     * 创建圆弧圆心阶段外轮廓预览对象。
     * @param points 外轮廓采样点。
     * @returns 外轮廓虚线对象。
     */
    private createArcCenterGuideCircle(points: THREE.Vector3[]): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const guideCircle = new THREE.Line(
            geometry,
            createArcDashedPreviewMaterial(points, '圆弧圆心阶段预览-屏幕空间虚线外轮廓材质'),
        );
        syncScreenSpaceDashedLineGeometry(guideCircle);
        return guideCircle;
    }

    /**
     * 创建圆弧圆心阶段半径引导线预览对象。
     * @param points 半径引导线点列。
     * @returns 半径引导细点画线对象。
     */
    private createArcCenterGuideRadiusLine(points: THREE.Vector3[]): THREE.Line {
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const guideLine = new THREE.Line(geometry, createArcCenterRadiusGuideMaterial(points));
        syncThinChainLineGeometry(guideLine);
        return guideLine;
    }

    /**
     * 更新屏幕空间虚线预览对象几何。
     * @param line 待更新的预览线。
     * @param points 新点列。
     */
    private updateScreenSpaceDashedPreviewLine(line: THREE.Line, points: THREE.Vector3[]): void {
        this.updatePreviewLineGeometry(line, points);
        syncScreenSpaceDashedLineGeometry(line);
    }

    /**
     * 更新细点画线预览对象几何。
     * @param line 待更新的预览线。
     * @param points 新点列。
     */
    private updateThinChainPreviewLine(line: THREE.Line, points: THREE.Vector3[]): void {
        this.updatePreviewLineGeometry(line, points);
        syncThinChainLineGeometry(line);
    }

    /**
     * 原地更新预览线几何。
     * @param line 待更新的预览线。
     * @param points 新点列。
     */
    private updatePreviewLineGeometry(line: THREE.Line, points: THREE.Vector3[]): void {
        const positionAttribute = line.geometry.getAttribute('position');
        if (positionAttribute instanceof THREE.BufferAttribute && positionAttribute.count === points.length) {
            points.forEach((point, index) => {
                positionAttribute.setXYZ(index, point.x, point.y, point.z);
            });
            positionAttribute.needsUpdate = true;
        } else {
            line.geometry.setFromPoints(points);
        }

        line.geometry.computeBoundingSphere();
    }


    /**
     * 在处理器内创建“圆心起止”圆弧预览。
     */
    private createArcPreviewByCenterInHandler(
        center: THREE.Vector3,
        start: THREE.Vector3,
        end: THREE.Vector3,
    ): void {
        const localCenter = this.referencePlane.worldToLocal(center);
        const localStart = this.referencePlane.worldToLocal(start);
        const localEnd = this.referencePlane.worldToLocal(end);
        const radius = Math.hypot(localStart.x - localCenter.x, localStart.y - localCenter.y);
        if (!Number.isFinite(radius) || radius <= SKETCH_GEOMETRY_TOLERANCE_CONFIG.curveLinearLength) {
            this.destroyPreview();
            return;
        }
        // 起点与终点重合时圆弧预览退化为零扫掠角，与实体创建逻辑保持一致，不渲染预览。
        const startEndDx = localStart.x - localEnd.x;
        const startEndDy = localStart.y - localEnd.y;
        if (startEndDx * startEndDx + startEndDy * startEndDy
            < SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition * SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition) {
            this.destroyPreview();
            return;
        }
        let startAngle = Math.atan2(localStart.y - localCenter.y, localStart.x - localCenter.x);
        let endAngle = Math.atan2(localEnd.y - localCenter.y, localEnd.x - localCenter.x);
        if (endAngle > startAngle) {
            endAngle -= Math.PI * 2;
        }
        const angleRange = Math.abs(startAngle - endAngle);
        const points = this.buildArcPolylineFromLocal(
            localCenter,
            radius,
            startAngle,
            endAngle,
            96,
        );
        this.renderArcPolylineInHandler(points, false);
        this.updateArcParameters(center, start, end, radius, angleRange);
    }



    /**
     * 在处理器内创建“三点圆弧”预览。
     */
    private createArcPreviewByThreePointsInHandler(
        start: THREE.Vector3,
        end: THREE.Vector3,
        through: THREE.Vector3,
    ): void {
        const localStart = this.referencePlane.worldToLocal(start);
        const localEnd = this.referencePlane.worldToLocal(end);
        const localThrough = this.referencePlane.worldToLocal(through);
        const arcData = buildArcFromThreePoints2D(localStart, localEnd, localThrough);
        if (!arcData) {
            this.destroyPreview();
            return;
        }
        const points = this.buildArcPolylineFromLocal(
            arcData.center,
            arcData.radius,
            arcData.startAngle,
            arcData.endAngle,
            96,
        );
        this.renderArcPolylineInHandler(points, false);
        const centerWorld = this.referencePlane.localToWorld(new THREE.Vector2(arcData.center.x, arcData.center.y));
        this.updateArcParameters(
            centerWorld,
            start,
            end,
            arcData.radius,
            arcData.angleRange,
        );
    }

    /**
     * 在处理器内创建“切线圆弧”预览。
     */
    private createArcPreviewByTangentInHandler(
        start: THREE.Vector3,
        end: THREE.Vector3,
        tangent: THREE.Vector3,
    ): void {
        const tangentData = this.buildTangentArcDataByPolicy(
            start,
            end,
            tangent,
            this.resolveTangentArcDirectionPolicy(),
        );
        if (!tangentData) {
            this.destroyPreview();
            return;
        }
        const points = this.buildArcPolylineFromLocal(
            tangentData.centerLocal,
            tangentData.radius,
            tangentData.startAngle,
            tangentData.endAngle,
            96,
        );
        this.renderArcPolylineInHandler(points, false);
        this.updateArcParameters(
            tangentData.centerWorld,
            start,
            end,
            tangentData.radius,
            tangentData.angleRange,
        );
    }

    /**
     * 构建局部坐标系下的圆弧折线点。
     */
    private buildArcPolylineFromLocal(
        localCenter: THREE.Vector2,
        radius: number,
        startAngle: number,
        endAngle: number,
        segments: number,
    ): THREE.Vector3[] {
        const points: THREE.Vector3[] = [];
        for (let i = 0; i <= segments; i += 1) {
            const t = i / segments;
            const angle = startAngle + (endAngle - startAngle) * t;
            const x = localCenter.x + radius * Math.cos(angle);
            const y = localCenter.y + radius * Math.sin(angle);
            points.push(this.referencePlane.localToWorld(new THREE.Vector2(x, y)));
        }
        return points;
    }

    /**
     * 渲染圆弧折线预览。
     */
    private renderArcPolylineInHandler(points: THREE.Vector3[], isConstruction: boolean): void {
        this.destroyPreview();
        if (points.length < 2) {
            return;
        }
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        const material = isConstruction
            ? new THREE.LineDashedMaterial({
                color: ARC_PREVIEW_COLOR,
                linewidth: 1,
                dashSize: 3,
                gapSize: 2,
                opacity: 0.8,
                transparent: true,
            })
            : new THREE.LineBasicMaterial({
                color: ARC_PREVIEW_COLOR,
                linewidth: 2,
                opacity: 0.8,
                transparent: true,
            });
        this.sketchDrawingManager.previewCircle = new THREE.Line(geometry, material);
        if (material instanceof THREE.LineDashedMaterial) {
            this.sketchDrawingManager.previewCircle.computeLineDistances();
        }
        this.viewer.addObject(this.sketchDrawingManager.previewCircle);
    }

    /**
     * 在处理器内创建圆弧实体。
     */
    private createArcEntityByHandler(): void {
        if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
            this.createCenterStartEndArcEntityByHandler();
            return;
        }
        if (this.arcOptions.mode === SketchArcCommand.ThreePoint) {
            this.createThreePointArcEntityByHandler();
        }
    }

    public buildArcWorldPoints(
        centerLocal: { x: number; y: number },
        radius: number,
        startAngle: number,
        endAngle: number,
        clockwise: boolean,
    ): { points: THREE.Vector3[]; angleRange: number } {
        const normalized = normalizeArcAngles(startAngle, endAngle, clockwise);
        const angleRange = normalized.angleRange;
        const steps = Math.max(16, Math.ceil(64 * Math.abs(angleRange) / (Math.PI * 2)));
        const arcPoints: THREE.Vector3[] = [];
        for (let i = 0; i <= steps; i += 1) {
            const t = i / steps;
            const angle = clockwise
                ? normalized.start - angleRange * t
                : normalized.start + angleRange * t;
            const localX = centerLocal.x + radius * Math.cos(angle);
            const localY = centerLocal.y + radius * Math.sin(angle);
            const worldPoint = this.referencePlane.localToWorld(localX, localY,);
            arcPoints.push(worldPoint);
        }
        return {points: arcPoints, angleRange};
    }
    /**
     * 在处理器内创建“圆心-起点-终点”圆弧实体。
     */
    private createCenterStartEndArcEntityByHandler(): void {
        if (this.drawingPoints.length < 3) {
            return;
        }
        const center = this.drawingPoints[0];
        const startPoint = this.drawingPoints[1];
        const endPoint = this.drawingPoints[2];
        const radius = center.distanceTo(startPoint);

        const localCenter = this.referencePlane.worldToLocal(center);
        const localStart = this.referencePlane.worldToLocal(startPoint);
        const localEnd = this.referencePlane.worldToLocal(endPoint);
        // 起点与终点重合时圆弧退化为零扫掠角（角度范围 0），按 SolidWorks 行为不创建圆弧对象。
        const startEndDx = localStart.x - localEnd.x;
        const startEndDy = localStart.y - localEnd.y;
        if (startEndDx * startEndDx + startEndDy * startEndDy
            < SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition * SKETCH_GEOMETRY_TOLERANCE_CONFIG.closedCurvePosition) {
            return;
        }
        const startAngleRaw = Math.atan2(localStart.y - localCenter.y, localStart.x - localCenter.x);
        const endAngleRaw = Math.atan2(localEnd.y - localCenter.y, localEnd.x - localCenter.x);
        const normalized = normalizeArcAngles(startAngleRaw, endAngleRaw, true);
        const arcPoints = this.buildArcWorldPoints(
            localCenter,
            radius,
            normalized.start,
            normalized.end,
            true,
        );

        // 将终点投影到圆弧上，确保终点控制点在圆弧曲线上而非用户点击的原始位置。
        const projectedEndLocalX = localCenter.x + radius * Math.cos(normalized.end);
        const projectedEndLocalY = localCenter.y + radius * Math.sin(normalized.end);
        const projectedEndWorld = this.referencePlane.localToWorld(
            new THREE.Vector2(projectedEndLocalX, projectedEndLocalY),
        );

        const isConstruction = this.arcOptions.forConstruction;
        const material = this.createSketchLineMaterial(isConstruction, false);
        const startControlPoint = this.resolveReusableArcControlPoint(1, startPoint)
            ?? this.createLineControlPoint(
                startPoint,
                false,
                this.resolveArcControlPointSource(1, startPoint),
            );
        const endControlPoint = this.resolveReusableArcControlPoint(2, projectedEndWorld)
            ?? this.createLineControlPoint(
                projectedEndWorld,
                false,
                this.resolveArcControlPointSource(2, projectedEndWorld),
            );
        const centerControlPoint = this.resolveReusableArcControlPoint(0, center)
            ?? this.createLineControlPoint(
                center,
                false,
                this.resolveArcControlPointSource(0, center),
            );
        const line = new SketchArcObject({
            startPoint: startControlPoint,
            endPoint: endControlPoint,
            centerPoint: centerControlPoint,
            material,
            referencePlane: this.referencePlane,
            definition: {
                centerWorld: center.clone(),
                radius,
                startAngle: normalized.start,
                endAngle: normalized.end,
                clockwise: true,
                segments: 64,
            },
        });
        line.updateArc();


        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(startControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(endControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(centerControlPoint);

        this.viewer.addSketchObject(line);
        this.sketchDrawingManager.notifySketchEntityCreated(line);
        this.tryFinalizeClosedArcCollection(line);
        this.updateArcParameters(center, startPoint, projectedEndWorld, radius, arcPoints.angleRange);
        // 创建完成后程序化选中新圆弧，使属性面板参数可编辑。
        this.viewer.entitySelectionManager?.selectEntityProgrammatically(line);
    }

    /**
     * 在处理器内创建“三点”圆弧实体。
     */
    private createThreePointArcEntityByHandler(): void {
        if (this.drawingPoints.length < 3) {
            return;
        }
        const startPoint = this.drawingPoints[0];
        const endPoint = this.drawingPoints[1];
        const throughPoint = this.drawingPoints[2];
        const localStart = this.referencePlane.worldToLocal(startPoint);
        const localEnd = this.referencePlane.worldToLocal(endPoint);
        const localThrough = this.referencePlane.worldToLocal(throughPoint);
        const arcData = buildArcFromThreePoints2D(localStart, localEnd, localThrough);
        if (!arcData) {
            return;
        }

        const arcPoints = this.buildArcWorldPoints(
            arcData.center,
            arcData.radius,
            arcData.startAngle,
            arcData.endAngle,
            arcData.clockwise,
        );
        const centerWorld = this.referencePlane.localToWorld(new THREE.Vector2(arcData.center.x, arcData.center.y));

        const isConstruction = this.arcOptions.forConstruction;
        const material = this.createSketchLineMaterial(isConstruction, false);
        const startControlPoint = this.resolveReusableArcControlPoint(0, startPoint)
            ?? this.createLineControlPoint(
                startPoint,
                false,
                this.resolveArcControlPointSource(0, startPoint),
            );
        const endControlPoint = this.resolveReusableArcControlPoint(1, endPoint)
            ?? this.createLineControlPoint(
                endPoint,
                false,
                this.resolveArcControlPointSource(1, endPoint),
            );
        const centerControlPoint = this.createLineControlPoint(centerWorld,);
        const line = new SketchArcObject({
            startPoint: startControlPoint,
            endPoint: endControlPoint,
            centerPoint: centerControlPoint,
            material,
            referencePlane: this.referencePlane,
            definition: {
                centerWorld: centerWorld.clone(),
                radius: arcData.radius,
                startAngle: arcData.startAngle,
                endAngle: arcData.endAngle,
                clockwise: arcData.clockwise,
                segments: 64,
            },
        });
        line.updateArc();


        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(line);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(startControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(endControlPoint);
        this.viewer.interactionStateManager?.syncExternalMaterialSnapshot(centerControlPoint);

        this.viewer.addSketchObject(line);
        this.sketchDrawingManager.notifySketchEntityCreated(line);
        this.tryFinalizeClosedArcCollection(line);
        this.updateArcParameters(centerWorld, startPoint, endPoint, arcData.radius, arcPoints.angleRange);
        // 创建完成后程序化选中新圆弧，使属性面板参数可编辑。
        this.viewer.entitySelectionManager?.selectEntityProgrammatically(line);
    }


    /**
     * 判断是否允许吸附到闭合起点。
     * @returns 是否允许。
     */
    protected canSnapToClosureStart(): boolean {
        const points = this.drawingPoints;
        if (this.arcOptions.mode === SketchArcCommand.Tangent) {
            return this.arcChainSegmentCount >= 1;
        }
        if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
            return points.length >= 2;
        }
        if (this.arcOptions.mode === SketchArcCommand.ThreePoint) {
            return points.length === 1;
        }
        return false;
    }

    /**
     * 获取闭合吸附链起点。
     * @returns 起点坐标；不存在返回 null。
     */
    protected override resolveChainStartPointForClosure(): THREE.Vector3 | null {
        const points = this.drawingPoints;
        if (this.arcOptions.mode === SketchArcCommand.Tangent) {
            if (this.arcChainStartPoint) {
                return this.arcChainStartPoint.clone();
            }
            return points.length >= 1 ? points[0].clone() : null;
        }
        if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
            return points.length >= 2 ? points[1].clone() : null;
        }
        if (this.arcOptions.mode === SketchArcCommand.ThreePoint) {
            return points.length === 1 ? points[0].clone() : null;
        }
        return null;
    }

    /**
     * 获取当前线段起点（用于闭合检测）。
     * @returns 起点坐标；不存在返回 null。
     */
    protected override resolveSegmentStartPointForClosure(): THREE.Vector3 | null {
        const points = this.drawingPoints;
        if (this.arcOptions.mode === SketchArcCommand.Tangent) {
            return points.length >= 1 ? points[0].clone() : null;
        }
        if (this.arcOptions.mode === SketchArcCommand.CenterStartEnd) {
            return points.length >= 2 ? points[1].clone() : null;
        }
        if (this.arcOptions.mode === SketchArcCommand.ThreePoint) {
            return points.length === 1 ? points[0].clone() : null;
        }
        return null;
    }


    /**
     * 从当前捕捉端点解析切线弧起点。
     * 说明：
     * 1. SOLIDWORKS 切线弧从已有草图实体端点开始；
     * 2. 当前绘制管理器已在 mousemove / mousedown 阶段完成端点捕捉，本方法只消费该端点语义；
     * 3. 来源边支持直线、圆弧、椭圆弧、抛物线、样条与样式样条，
     *    端点切线沿曲线在该端点处的延续方向计算。
     * @param targetPoint 当前鼠标点击命中的捕捉端点。
     * @returns 切线弧起点信息；无法解析时返回 null。
     */
    private resolveTangentArcStartInfo(
        targetPoint: SketchPointObject | null | undefined,
    ): TangentArcStartInfo | null {
        if (!targetPoint || !targetPoint.isEndPoint || targetPoint.isDrawingPreviewPoint) {
            return null;
        }

        const sourceEdge = this.resolveTangentArcSourceEdge(targetPoint);
        if (!sourceEdge) {
            return null;
        }

        const tangent = this.resolveTangentArcEndpointTangent(sourceEdge, targetPoint);
        if (!tangent) {
            return null;
        }

        return {
            pointObject: targetPoint,
            point: targetPoint.getPosition(),
            tangent,
            sourceEdge,
        };
    }

    /**
     * 从端点关联边中解析切线弧来源草图边。
     * 说明：
     * 1. 端点可能同时连接多条边，当前捕捉载荷没有携带被点击的边对象；
     * 2. 因此按关联边顺序选择第一条正式来源边（直线/圆弧/椭圆弧/抛物线/样条），
     *    保证单边端点场景稳定可用；
     * 3. 绘制预览直线必须排除，避免切线弧从临时几何起步。
     * @param targetPoint 捕捉端点。
     * @returns 来源草图边；不存在时返回 null。
     */
    private resolveTangentArcSourceEdge(targetPoint: SketchPointObject): TangentArcSourceEdge | null {
        for (const edge of targetPoint.getConnectedEdgesSnapshot()) {
            if (!isTangentArcSourceEdge(edge)) {
                continue;
            }
            // 预览线只在直线实体上存在；其余曲线类型没有绘制预览实体。
            if (isSketchLineObject(edge) && edge.isDrawingPreviewLine) {
                continue;
            }
            return edge;
        }
        return null;
    }

    /**
     * 计算来源草图边在指定端点处的切线弧起步方向。
     * 说明：
     * 1. 行进切线由边的几何类型解析（直线沿整体方向、圆弧/椭圆弧沿扫掠方向、
     *    抛物线沿参数增大方向、样条沿采样点列方向）；
     * 2. 选中终点时沿行进方向延续，选中起点时取反，使切线弧从被选端点
     *    沿原曲线的延续方向起步（与既有直线语义一致）。
     * @param sourceEdge 来源草图边。
     * @param targetPoint 被选端点。
     * @returns 单位切线方向；边退化或端点不属于该边时返回 null。
     */
    private resolveTangentArcEndpointTangent(
        sourceEdge: TangentArcSourceEdge,
        targetPoint: SketchPointObject,
    ): THREE.Vector3 | null {
        const isEndPoint = this.isSourceEdgeEndPoint(sourceEdge, targetPoint);
        if (!isEndPoint && !this.isSourceEdgeStartPoint(sourceEdge, targetPoint)) {
            return null;
        }

        const travelTangent = resolveEdgeTravelTangentAtEndpoint(sourceEdge, targetPoint);
        if (!travelTangent
            || travelTangent.lengthSq() < SKETCH_GEOMETRY_TOLERANCE_CONFIG.lineDirectionLengthSquared) {
            return null;
        }

        // 终点沿行进方向延续，起点沿行进方向的反向延续。
        return isEndPoint ? travelTangent : travelTangent.multiplyScalar(-1);
    }

    /**
     * 判断指定点是否为来源草图边的起点。
     * @param sourceEdge 来源草图边。
     * @param targetPoint 待判断点。
     * @returns 是否为起点。
     */
    private isSourceEdgeStartPoint(sourceEdge: TangentArcSourceEdge, targetPoint: SketchPointObject): boolean {
        return getSourceEdgeStartPoint(sourceEdge) === targetPoint;
    }

    /**
     * 判断指定点是否为来源草图边的终点。
     * @param sourceEdge 来源草图边。
     * @param targetPoint 待判断点。
     * @returns 是否为终点。
     */
    private isSourceEdgeEndPoint(sourceEdge: TangentArcSourceEdge, targetPoint: SketchPointObject): boolean {
        return getSourceEdgeEndPoint(sourceEdge) === targetPoint;
    }


    /**
     * 解析当前绘制点是否命中了可记录为来源的草图控制点。
     * 说明：
     * 1. 仅当捕捉目标点对象与最终圆弧控制点仍在容差内时才记录来源；
     * 2. 若圆弧解算后端点被投影或修正到其它位置，则放弃记录；
     * 3. 该方法只返回来源引用，不把捕捉点直接复用为圆弧控制点。
     * @param pointIndex 当前绘制点索引。
     * @param expectedPoint 圆弧控制点目标点位。
     * @returns 可记录的来源点对象；否则返回 null。
     */
    private resolveArcControlPointSource(
        pointIndex: number,
        expectedPoint: THREE.Vector3,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex];
        return this.resolveControlPointSource(targetPoint, expectedPoint);
    }

    /**
     * 解析当前绘制点是否命中了可直接复用的线段端点。
     * @param pointIndex 当前绘制点索引。
     * @param expectedPoint 圆弧控制点目标点位。
     * @returns 可复用的线段端点对象；否则返回 null。
     */
    private resolveReusableArcControlPoint(
        pointIndex: number,
        expectedPoint: THREE.Vector3,
    ): SketchPointObject | null {
        const targetPoint = this.drawingPointTargets[pointIndex];
        return this.resolveReusableLineEndpointControlPoint(targetPoint, expectedPoint);
    }


    /**
     * 尝试基于新创建圆弧收口闭合集合。
     * 说明：
     * 1. 普通圆弧与切线弧统一复用草图组闭合入口；
     * 2. 切线弧是否结束连续绘制由调用方根据返回值处理，本方法只负责集合收口；
     * 3. 构造圆弧不参与有效轮廓闭合；
     * 4. 若解析成功则创建 `SketchCollectionObject` 并同步 contour。
     * @param arcObject 新创建的圆弧对象。
     * @returns 成功创建闭合集合时返回 true，否则返回 false。
     */
    private tryFinalizeClosedArcCollection(arcObject: SketchArcObject): boolean {
        if (arcObject.isConstruction) {
            return false;
        }

        const activeSketchGroup = this.viewer.sketchGroupManager?.activeSketchGroup;
        if (!activeSketchGroup) {
            return false;
        }

        const collectionObject = activeSketchGroup.tryFinalizeClosedSketchCollection(
            [arcObject],
            {syncContour: this.viewer.shadedSketchContoursEnabled},
        );
        if (!collectionObject) {
            return false;
        }

        this.sketchDrawingManager.notifySketchEntityCreated(collectionObject);
        this.viewer.syncShadedContoursAfterSketchEntityCreated(activeSketchGroup);
        return true;
    }


}


